import {
  alpha,
  Box,
  CircularProgress,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import RadarIcon from "@mui/icons-material/Radar";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import React, { useCallback, useEffect, useState } from "react";
import { getDeliveryByRadius } from "../../../../api/DeliveryAnalyticsService";

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);

const formatCurrencyShort = (value) => {
  if (value >= 1000) return `R$${(value / 1000).toFixed(1)}k`;
  return `R$${value.toFixed(0)}`;
};

const KpiCard = ({ icon, label, value, color }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: alpha(color, 0.08),
        border: `1px solid ${alpha(color, 0.2)}`,
        textAlign: "center",
      }}
    >
      <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} sx={{ color }}>
        {value}
      </Typography>
    </Box>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        p: 1.5,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        boxShadow: 2,
        minWidth: 200,
      }}
    >
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {label}
      </Typography>
      {payload.map((entry, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            py: 0.25,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: entry.type === "line" ? "50%" : "2px",
                bgcolor: entry.color,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {entry.name}
            </Typography>
          </Box>
          <Typography variant="body2" fontWeight={600}>
            {entry.dataKey === "orderCount"
              ? entry.value
              : formatCurrency(entry.value)}
          </Typography>
        </Box>
      ))}
      {(() => {
        const taxEntry = payload.find((p) => p.dataKey === "deliveryTaxTotal");
        const payEntry = payload.find(
          (p) => p.dataKey === "driverPaymentTotal"
        );
        if (taxEntry && payEntry) {
          const profit = taxEntry.value - payEntry.value;
          return (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                pt: 0.75,
                mt: 0.75,
                borderTop: "1px dashed",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Lucro
              </Typography>
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{ color: profit >= 0 ? "success.main" : "error.main" }}
              >
                {formatCurrency(profit)}
              </Typography>
            </Box>
          );
        }
        return null;
      })()}
    </Box>
  );
};

export const DeliveryByRadiusStats = ({ startDate, endDate }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      const params = { start_date: startDate, end_date: endDate };
      const response = await getDeliveryByRadius(params);
      setData(response.data);
    } catch (err) {
      console.error("Error loading delivery by radius data:", err);
      setError("Não foi possível carregar os dados por raio de entrega");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <RadarIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
        <Typography color="text.secondary">
          Nenhum dado de entrega por raio disponível no período
        </Typography>
      </Box>
    );
  }

  // Totals include ALL data (including "Sem distância") to match Geral tab
  const totalOrders = data.reduce((sum, d) => sum + d.orderCount, 0);
  const totalTax = data.reduce(
    (sum, d) => sum + parseFloat(d.deliveryTaxTotal),
    0
  );
  const totalDriverPayment = data.reduce(
    (sum, d) => sum + parseFloat(d.driverPaymentTotal),
    0
  );
  const totalProfit = totalTax - totalDriverPayment;

  // Chart only shows orders with distance data
  const chartData = data
    .filter((d) => d.radiusKm !== "Sem distância")
    .map((d) => ({
      ...d,
      deliveryTaxTotal: parseFloat(d.deliveryTaxTotal),
      driverPaymentTotal: parseFloat(d.driverPaymentTotal),
      profitTotal: parseFloat(d.profitTotal),
    }));

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <RadarIcon sx={{ mr: 1, color: "primary.main" }} />
        <Typography variant="h6">Entregas por Raio (km)</Typography>
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <KpiCard
            icon={<LocalShippingIcon fontSize="small" />}
            label="Entregas"
            value={totalOrders.toLocaleString("pt-BR")}
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard
            icon={<AttachMoneyIcon fontSize="small" />}
            label="Tx Cobrada"
            value={formatCurrency(totalTax)}
            color={theme.palette.success.main}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard
            icon={<AccountBalanceWalletIcon fontSize="small" />}
            label="Pago Entregadores"
            value={formatCurrency(totalDriverPayment)}
            color={theme.palette.warning.main}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard
            icon={<TrendingUpIcon fontSize="small" />}
            label="Lucro"
            value={formatCurrency(totalProfit)}
            color={
              totalProfit >= 0
                ? theme.palette.success.dark
                : theme.palette.error.main
            }
          />
        </Grid>
      </Grid>

      <Box sx={{ height: 350 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis
              dataKey="radiusKm"
              tick={{ fontSize: 12 }}
              tickLine={false}
            />
            <YAxis
              yAxisId="left"
              orientation="left"
              tick={{ fontSize: 11 }}
              tickFormatter={formatCurrencyShort}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              iconType="square"
              wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
            />
            <Bar
              yAxisId="left"
              name="Taxa cobrada"
              dataKey="deliveryTaxTotal"
              fill={theme.palette.success.main}
              opacity={0.85}
              radius={[4, 4, 0, 0]}
              barSize={28}
            />
            <Bar
              yAxisId="left"
              name="Pago ao entregador"
              dataKey="driverPaymentTotal"
              fill={theme.palette.warning.main}
              opacity={0.85}
              radius={[4, 4, 0, 0]}
              barSize={28}
            />
            <Line
              yAxisId="right"
              name="Entregas"
              dataKey="orderCount"
              type="monotone"
              stroke={theme.palette.primary.main}
              strokeWidth={2.5}
              dot={{
                r: 4,
                fill: theme.palette.primary.main,
                strokeWidth: 2,
                stroke: "#fff",
              }}
              activeDot={{ r: 6 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </>
  );
};
